import { computed, ref, type Ref } from 'vue';

export type WizardNestedMode = 'standalone' | 'nested';

export type WizardNestedParent = {
  suspend: () => void;
  resume: () => void;
};

export type WizardNestedRunOptions = {
  hideParent?: WizardNestedParent;
};

export function createWizardNestedParent(suspended: Ref<boolean>): WizardNestedParent {
  return {
    suspend: () => {
      suspended.value = true;
    },
    resume: () => {
      suspended.value = false;
    },
  };
}

export function createWizardNestedSession<TResult>() {
  const mode = ref<WizardNestedMode>('standalone');
  let pending: ((result: TResult | null) => void) | null = null;
  let parent: WizardNestedParent | null = null;

  function finish(result: TResult | null) {
    const resolve = pending;
    const currentParent = parent;
    pending = null;
    parent = null;
    mode.value = 'standalone';
    resolve?.(result);

    if (currentParent) {
      queueMicrotask(() => {
        if (parent !== currentParent) {
          currentParent.resume();
        }
      });
    }
  }

  return {
    mode,
    isNested: computed(() => mode.value === 'nested'),
    runNested: (open: () => void, options?: WizardNestedRunOptions) =>
      new Promise<TResult | null>((resolve) => {
        finish(null);
        mode.value = 'nested';
        pending = resolve;
        parent = options?.hideParent ?? null;
        parent?.suspend();
        open();
      }),
    settle: (result: TResult | null) => {
      if (pending) {
        finish(result);
      } else {
        mode.value = 'standalone';
      }
    },
    cancel: () => finish(null),
  };
}

export type WizardNestedSession<TResult> = ReturnType<typeof createWizardNestedSession<TResult>>;
